// Shared, fully server-rendered layout for the editorial "best of" round-ups.

import { Link } from "@tanstack/react-router";
import { SiteFooter } from "@/components/site-footer";
import { LibraryHelpCta } from "@/components/library-help-cta";
import { SmartImage } from "@/components/smart-image";
import { hubSlug } from "@/lib/library-hubs";
import type { BestGem, BestList } from "@/lib/library-best";

export function BestListPage({ list, gems }: { list: BestList; gems: BestGem[] }) {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" className="mx-auto max-w-5xl px-5 py-10 md:px-8 md:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/" className="transition-colors hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden>/</li>
            <li>
              <Link to="/library" className="transition-colors hover:text-foreground">
                The Vibeary
              </Link>
            </li>
            <li aria-hidden>/</li>
            <li>
              <a href="/library/best" className="transition-colors hover:text-foreground">
                Round-ups
              </a>
            </li>
            <li aria-hidden>/</li>
            <li className="text-foreground">{list.title}</li>
          </ol>
        </nav>

        <p className="mt-8 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          The Vibeary — round-up
        </p>
        <h1
          className="mt-4 font-display tracking-[-0.035em] leading-[1.04] max-w-[22ch]"
          style={{ fontSize: "clamp(2rem, 4.6vw, 3.6rem)" }}
        >
          {list.title}
        </h1>
        <p className="mt-6 max-w-[62ch] text-base md:text-lg leading-relaxed text-muted-foreground text-pretty">
          {list.intro}
        </p>

        <section className="mt-10 rounded-2xl border border-border p-5 md:p-6">
          <h2 className="font-display text-lg tracking-tight">What earned a place</h2>
          <ul className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
            {list.criteria.map((c) => (
              <li key={c} className="flex gap-2">
                <span aria-hidden className="text-foreground">
                  —
                </span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl tracking-tight">
            {gems.length} picks from the library
          </h2>
          <ol className="mt-6 divide-y divide-border border-y border-border">
            {gems.map((g, i) => (
              <li key={g.slug} className="grid gap-4 py-6 sm:grid-cols-[3rem_1fr] md:gap-6">
                <div className="tabular-nums text-sm text-muted-foreground pt-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-xl tracking-tight">
                    <a href={`/library/${g.slug}`} className="hover:underline">
                      {g.name}
                    </a>
                  </h3>
                  <p className="mt-2 max-w-[68ch] text-sm leading-relaxed text-muted-foreground">
                    {g.description}
                  </p>
                  {g.why_gem ? (
                    <p className="mt-2 max-w-[68ch] text-sm leading-relaxed">
                      <span className="text-muted-foreground">Why it made the list: </span>
                      {g.why_gem}
                    </p>
                  ) : null}
                  <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <a
                      href={`/library/c/${hubSlug(g.category)}/${hubSlug(g.subcategory)}`}
                      className="underline underline-offset-2 hover:text-foreground"
                    >
                      {g.subcategory}
                    </a>
                    {g.license ? <span>{g.license}</span> : null}
                    {g.author ? <span>{g.author}</span> : null}
                    <a
                      href={g.url}
                      rel="noopener noreferrer nofollow"
                      target="_blank"
                      className="underline underline-offset-2 hover:text-foreground"
                    >
                      {g.domain}
                    </a>
                  </p>
                  {g.og_card_url || g.og_image_url ? (
                    <a
                      href={`/library/${g.slug}`}
                      className="mt-4 block overflow-hidden rounded-xl border border-border"
                    >
                      <SmartImage
                        src={(g.og_card_url ?? g.og_image_url) as string}
                        alt={`${g.name} preview`}
                        width={1200}
                        height={675}
                        priority={i < 2}
                        className="aspect-[16/9] w-full object-cover"
                      />
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        </section>

        {list.faq.length > 0 ? (
          <section className="mt-14">
            <h2 className="font-display text-2xl tracking-tight">Questions</h2>
            <dl className="mt-6 space-y-6">
              {list.faq.map((f) => (
                <div key={f.q}>
                  <dt className="font-medium">{f.q}</dt>
                  <dd className="mt-2 max-w-[68ch] text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        <section className="mt-14 flex flex-wrap gap-3 text-sm">
          <a
            href="/library/best"
            className="inline-flex min-h-[40px] items-center rounded-full border border-border px-4 transition-colors hover:bg-muted"
          >
            All round-ups
          </a>
          <Link
            to="/library"
            className="inline-flex min-h-[40px] items-center rounded-full border border-border px-4 transition-colors hover:bg-muted"
          >
            Search all gems
          </Link>
          <a
            href="/library/new"
            className="inline-flex min-h-[40px] items-center rounded-full border border-border px-4 transition-colors hover:bg-muted"
          >
            Newest additions
          </a>
        </section>
        <div className="mx-auto w-full max-w-6xl px-5 pb-16 md:px-8">
          <LibraryHelpCta line="We're full-stack designers and a vibe coding agency. We pick tools like these for client builds every week — bring your project and we'll design it, build it and ship it." />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

export function bestJsonLd(list: BestList, gems: BestGem[], url: string) {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: list.title,
    description: list.metaDescription,
    url,
    numberOfItems: gems.length,
    itemListElement: gems.map((g, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: g.name,
      url: `https://vibing.inc/library/${g.slug}`,
    })),
  });
}
